const LOGIN_TENANT_ROUTE = /^\/login\/([^/?#]+)\/?$/i;
const RESET_PASSWORD_TENANT_ROUTE = /^\/resetPassword\/([^/?#]+)\/?$/i;
const ROOT_TENANT_ROUTE = /^\/([^/?#]+)\/?$/i;
/** Platform admin: /tenants/{uuid}/details */
const TENANTS_ADMIN_ROUTE = /^\/tenants\/([^/?#]+)(?:\/|$)/i;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * First path segments that are app routes — never treated as tenant login slugs.
 * Keep in sync with platformRoutes / service routes.
 */
const RESERVED_TENANT_SLUGS = new Set([
  'login',
  'resetpassword',
  'first-login',
  'dashboard',
  'team',
  'contacts',
  'invoices',
  'form',
  'bar',
  'pie',
  'line',
  'faq',
  'calendar',
  'geography',
  'notfound',
  'sessions',
  'users',
  'userdetails',
  'profile',
  'tenants',
  'tenant-users',
  'tenant-users-admin',
  'groups',
  'permissions',
  'user-audit-trials',
  'services',
  'tenant-settings',
  'system-register',
  'api',
  'static',
  'favicon.ico',
  'logo.png',
  'manifest.json',
  'robots.txt',
  'runtime-config.js',
]);

function normalizeTenantId(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed !== '' ? trimmed : null;
}

function decodeTenantId(rawValue) {
  const normalized = normalizeTenantId(rawValue);
  if (!normalized) return null;
  try {
    return decodeURIComponent(normalized);
  } catch {
    return normalized;
  }
}

export function isTenantUuid(value) {
  const normalized = normalizeTenantId(value);
  return normalized != null && UUID_PATTERN.test(normalized);
}

/** True when the segment can be a tenant name or UUID in a public login URL. */
export function isTenantRouteKey(value) {
  const normalized = normalizeTenantId(value);
  if (!normalized) return false;
  if (isTenantUuid(normalized)) return true;
  const lower = normalized.toLowerCase();
  if (RESERVED_TENANT_SLUGS.has(lower)) return false;
  // Allow letters, digits, underscore, hyphen, dot, space (encoded in URLs)
  return /^[a-z0-9][a-z0-9._\- ]{0,63}$/i.test(normalized);
}

/**
 * Prefer a human tenant name for public URLs; fall back to UUID/key.
 * Example: buildTenantLoginPath('datahouse') → '/datahouse'
 */
export function buildTenantLoginPath(tenantKey) {
  const normalized = normalizeTenantId(tenantKey);
  return normalized ? `/${encodeURIComponent(normalized)}` : '/login';
}

export function buildTenantResetPasswordPath(tenantKey) {
  const normalized = normalizeTenantId(tenantKey);
  return normalized
    ? `/resetPassword/${encodeURIComponent(normalized)}`
    : '/resetPassword';
}

export function getTenantIdFromPathname(pathname) {
  const path = normalizeTenantId(pathname);
  if (!path) return null;

  const loginMatch = path.match(LOGIN_TENANT_ROUTE);
  if (loginMatch?.[1]) {
    const key = decodeTenantId(loginMatch[1]);
    return isTenantRouteKey(key) ? key : null;
  }

  const resetMatch = path.match(RESET_PASSWORD_TENANT_ROUTE);
  if (resetMatch?.[1]) {
    const key = decodeTenantId(resetMatch[1]);
    return isTenantRouteKey(key) ? key : null;
  }

  const tenantsAdminMatch = path.match(TENANTS_ADMIN_ROUTE);
  if (tenantsAdminMatch?.[1]) {
    // /tenants/{uuid}/details — registry admin, not public tenant slug in first segment
    const tenantId = decodeTenantId(tenantsAdminMatch[1]);
    return isTenantUuid(tenantId) ? tenantId : null;
  }

  // /{tenantName} or /{tenantName}/dashboard /users /system-register/...
  const parts = path.split('/').filter(Boolean);
  if (parts.length >= 1) {
    const key = decodeTenantId(parts[0]);
    if (isTenantRouteKey(key)) {
      return key;
    }
  }

  return null;
}

export function getTenantIdFromWindowLocation() {
  if (typeof window === 'undefined' || !window.location) return null;

  const fromQuery = getRegistryTenantIdFromSearch(window.location.search);
  if (fromQuery) return fromQuery;

  return getTenantIdFromPathname(window.location.pathname);
}

/** ?registryTenantId={uuid} on pages like /userDetails/{userId} */
export function getRegistryTenantIdFromSearch(search) {
  if (search == null || search === '') return null;
  const raw =
    search instanceof URLSearchParams
      ? search.get('registryTenantId')
      : new URLSearchParams(String(search).replace(/^\?/, '')).get('registryTenantId');
  const tenantId = decodeTenantId(raw);
  return isTenantUuid(tenantId) ? tenantId : null;
}

export function buildUserDetailsPath(userId, registryTenantId) {
  const uid = normalizeTenantId(userId);
  if (!uid) return '/users';
  const base = `/userDetails/${encodeURIComponent(uid)}`;
  const tid = normalizeTenantId(registryTenantId);
  if (tid && isTenantUuid(tid)) {
    return `${base}?registryTenantId=${encodeURIComponent(tid)}`;
  }
  return base;
}

export function readRouteTenantId(routeTenantId) {
  const key = decodeTenantId(routeTenantId);
  return isTenantRouteKey(key) ? key : null;
}

export function isTenantPublicEntryPath(pathname) {
  const path = normalizeTenantId(pathname);
  if (!path) return false;

  if (path.match(LOGIN_TENANT_ROUTE)) {
    const key = decodeTenantId(path.match(LOGIN_TENANT_ROUTE)[1]);
    return isTenantRouteKey(key);
  }
  if (path.match(RESET_PASSWORD_TENANT_ROUTE)) {
    const key = decodeTenantId(path.match(RESET_PASSWORD_TENANT_ROUTE)[1]);
    return isTenantRouteKey(key);
  }

  const rootMatch = path.match(ROOT_TENANT_ROUTE);
  if (rootMatch?.[1]) {
    return isTenantRouteKey(decodeTenantId(rootMatch[1]));
  }

  return false;
}

/**
 * Public URL key for a tenant: prefer registry name over UUID.
 */
export function resolveTenantPublicPathKey({ name, id, fallback } = {}) {
  const fromName = normalizeTenantId(name);
  if (fromName && isTenantRouteKey(fromName) && !isTenantUuid(fromName)) {
    return fromName;
  }
  const fromId = normalizeTenantId(id);
  if (fromId) return fromId;
  return normalizeTenantId(fallback);
}
